import type { TabsProps } from 'antd'
import { Tabs } from 'antd'
import { RcFile } from 'antd/es/upload'
import Modules from 'application/modules'
import { getACLDataFilter, isSU } from 'contexts/ACL/service'
import { I18nContext } from 'contexts/I18n/I18nProvider'
import { Roles } from 'core/auth/constants'
import { ClearConfigManagementDataFunction, DownloadConfigManagementFileFunction, File, GetConfigManagementDataFunction, RTFConfigurationManagementState } from 'modules/operational/rtf/redux/configManagement/types'
import { ClearConfigManagementLogsFunction, ConfigManagementLogsState, GetConfigManagementLogsFunction } from 'modules/operational/rtf/redux/logs/configManagement/types'
import { useContext, useEffect } from 'react'
import Field from '../common/Field'
import GridContainer from '../common/GridContainer'
import './index.scss'
import Logs from './Logs'
import Management from './Management'

enum ManagementTabs {
  management = 'management',
  log = 'log',
}

const tabsMap = {
  [Roles.OU]: [ManagementTabs.management],
  [Roles.SU]: [ManagementTabs.log],
}

type ConfigurationManagementProps = {
  configManagement: RTFConfigurationManagementState
  configManagementLogs: ConfigManagementLogsState
  getConfigManagementData: GetConfigManagementDataFunction
  clearConfigManagementData: ClearConfigManagementDataFunction
  downloadConfigManagementFile: DownloadConfigManagementFileFunction
  deleteConfigManagementFile: (file: File) => void
  invalidFileToaster: (title: string, message: string) => void
  uploadFile: (file: RcFile) => void
  getConfigManagementUploadLogs: GetConfigManagementLogsFunction
  clearConfigManagementUploadLogs: ClearConfigManagementLogsFunction
}

export default function ConfigurationManagement({
  configManagement, 
  configManagementLogs,
  getConfigManagementData, 
  clearConfigManagementData, 
  downloadConfigManagementFile, 
  invalidFileToaster, 
  uploadFile, 
  getConfigManagementUploadLogs, 
  clearConfigManagementUploadLogs,
  deleteConfigManagementFile}: ConfigurationManagementProps) {
  const t = useContext(I18nContext)

  const tabsForUser = getACLDataFilter(Modules.rtf, tabsMap[Roles.OU], tabsMap[Roles.SU])

  useEffect(() => {
    const load = async () => {
      await getConfigManagementData()

      if (isSU(Modules.rtf)) {
        getConfigManagementUploadLogs()
      }
    }

    load()

    return () => {
      clearConfigManagementData()
      clearConfigManagementUploadLogs()
    }
  }, [])

  const tabItems: TabsProps['items'] = [
    {
      key: ManagementTabs.management,
      label: t('rtf.configuration_management_tab'),
      children: <Management 
                  configManagement={configManagement} 
                  downloadConfigManagementFile={downloadConfigManagementFile} 
                  deleteConfigManagementFile={deleteConfigManagementFile} 
                  invalidFileToaster={invalidFileToaster} 
                  uploadFile={uploadFile}
                />,
    },
    {
      key: ManagementTabs.log,
      label: t('rtf.configuration_logs_tab'),
      children: <Logs
        getConfigManagementUploadLogs={getConfigManagementUploadLogs}
        configManagementLogs={configManagementLogs}
      />,
    },
  ]

  return (
    <GridContainer>
      <Field>
        {isSU(Modules.rtf) ? (
          <div className="rtf-configuration-management">
            <Tabs items={tabItems.filter(item => tabsForUser.includes(item.key))} />
          </div>
        ) : (
          <Management
            configManagement={configManagement}
            downloadConfigManagementFile={downloadConfigManagementFile}
            deleteConfigManagementFile={deleteConfigManagementFile}
            invalidFileToaster={invalidFileToaster}
            uploadFile={uploadFile}
          />
        )}
      </Field>
    </GridContainer>
  )
}
